import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, CheckSquare, X } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';

export default function IssueScanDialog({ open, onClose }) {
  const queryClient = useQueryClient();
  const [scanTypes, setScanTypes] = useState({
    mission_overlap: false,
    duplicate_objectives: false,
    duplicate_activities: false,
    duplicate_tasks: false,
    conflicting_missions: false,
    conflicting_objectives: false,
    conflicting_activities: false,
    conflicting_tasks: false,
    overloaded_officers: false,
    underutilized_officers: false,
    underutilized_artifacts: false
  });
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState([]);
  const [selectedIssues, setSelectedIssues] = useState({});

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  const { data: missions } = useQuery({
    queryKey: ['missions'],
    queryFn: () => base44.entities.Mission.list(),
    initialData: []
  });

  const { data: objectives } = useQuery({
    queryKey: ['objectives'],
    queryFn: () => base44.entities.Objective.list(),
    initialData: []
  });

  const { data: activities } = useQuery({
    queryKey: ['activities'],
    queryFn: () => base44.entities.Activity.list(),
    initialData: []
  });

  const { data: dailyTasks } = useQuery({
    queryKey: ['dailyTasks'],
    queryFn: () => base44.entities.DailyTask.list(),
    initialData: []
  });

  const { data: officers } = useQuery({
    queryKey: ['officers'],
    queryFn: () => base44.entities.OfficerProfile.list(),
    initialData: []
  });

  const { data: artifacts } = useQuery({
    queryKey: ['artifacts'],
    queryFn: () => base44.entities.Artifact.list(),
    initialData: []
  });

  const createIssuesMutation = useMutation({
    mutationFn: async (issues) => {
      const promises = issues.map(issue => 
        base44.entities.Issue.create({
          title: issue.title,
          description: issue.description,
          category: issue.category || 'execution_headwind',
          severity: issue.severity || 'medium',
          source_type: 'manual',
          source: 'scan',
          reported_by: user?.email,
          reported_at: new Date().toISOString(),
          status: 'open'
        })
      );
      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      setScanResults([]);
      setSelectedIssues({});
      onClose();
    }
  });

  const scanTypeLabels = {
    mission_overlap: 'Scan for overlap between missions',
    duplicate_objectives: 'Scan for duplicate or redundant objectives',
    duplicate_activities: 'Scan for duplicate or redundant activities',
    duplicate_tasks: 'Scan for duplicate or redundant tasks',
    conflicting_missions: 'Scan for conflicting missions',
    conflicting_objectives: 'Scan for conflicting objectives',
    conflicting_activities: 'Scan for conflicting activities',
    conflicting_tasks: 'Scan for conflicting tasks',
    overloaded_officers: 'Scan for overloaded officers',
    underutilized_officers: 'Scan for underutilized officers',
    underutilized_artifacts: 'Scan for underutilized artifacts'
  };

  const handleScan = async () => {
    const selectedScans = Object.keys(scanTypes).filter(key => scanTypes[key]);
    if (selectedScans.length === 0) return;

    setScanning(true);

    try {
      // Prepare context for AI
      const context = {
        missions: missions.map(m => ({ id: m.id, title: m.title, objective: m.objective, game_mode: m.game_mode })),
        objectives: objectives.map(o => ({ id: o.id, statement: o.statement, mission_id: o.mission_id, status: o.status })),
        activities: activities.map(a => ({ 
          id: a.id, 
          title: a.title, 
          objective_id: a.objective_id, 
          responsible_email: a.responsible_email,
          accountable_email: a.accountable_email,
          status: a.status 
        })),
        dailyTasks: dailyTasks.map(t => ({ 
          id: t.id, 
          task_description: t.task_description, 
          user_email: t.user_email, 
          date: t.date,
          activity_id: t.activity_id
        })),
        officers: officers.map(o => ({ 
          email: o.user_email, 
          callsign: o.callsign, 
          custom_role: o.custom_role 
        })),
        artifacts: artifacts.map(a => ({ 
          id: a.id, 
          name: a.name, 
          usage_count: a.usage_count || 0, 
          status: a.status 
        })),
        selectedScans: selectedScans.map(key => scanTypeLabels[key])
      };

      const prompt = `You are analyzing an organizational system to identify issues. Based on the following data and requested scans, identify specific, actionable issues:

DATA:
${JSON.stringify(context, null, 2)}

REQUESTED SCANS:
${selectedScans.map(key => `- ${scanTypeLabels[key]}`).join('\n')}

For each issue found, provide:
1. A clear, concise title (max 60 chars)
2. A detailed description explaining the issue and its impact
3. A category: "red_line_violation", "execution_headwind", "capability_gap", "process_friction", or "other"
4. A severity: "low", "medium", "high", or "critical"

IMPORTANT: In your descriptions, use the actual NAMES/TITLES of missions, objectives, and activities instead of their IDs. For example:
- Use "Mission: Improve Customer Satisfaction" instead of "Mission ID: abc123"
- Use 'Objective: "Increase sales by 20%"' instead of "Objective ID: xyz789"
- Use "Activity: Launch marketing campaign" instead of "Activity ID: def456"

Also reference officer callsigns instead of email addresses when possible.

Be specific and actionable. Only report genuine issues that require attention. If no issues found for a scan type, skip it.

Return an array of issue objects.`;

      const response = await base44.integrations.Core.InvokeLLM({
        prompt: prompt,
        response_json_schema: {
          type: "object",
          properties: {
            issues: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  title: { type: "string" },
                  description: { type: "string" },
                  category: { type: "string" },
                  severity: { type: "string" }
                },
                required: ["title", "description"]
              }
            }
          }
        }
      });

      const issues = response.issues || [];
      setScanResults(issues);
      
      // Select all by default
      const selected = {};
      issues.forEach((_, idx) => selected[idx] = true);
      setSelectedIssues(selected);

    } catch (error) {
      console.error('Scan failed:', error);
      alert('Failed to complete scan. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const handleAddSelectedIssues = () => {
    const issuesToAdd = scanResults.filter((_, idx) => selectedIssues[idx]);
    if (issuesToAdd.length > 0) {
      createIssuesMutation.mutate(issuesToAdd);
    }
  };

  const handleReset = () => {
    setScanResults([]);
    setSelectedIssues({});
    setScanTypes({
      mission_overlap: false,
      duplicate_objectives: false,
      duplicate_activities: false,
      duplicate_tasks: false,
      conflicting_missions: false,
      conflicting_objectives: false,
      conflicting_activities: false,
      conflicting_tasks: false,
      overloaded_officers: false,
      underutilized_officers: false,
      underutilized_artifacts: false
    });
  };

  const selectedCount = Object.values(selectedIssues).filter(Boolean).length;
  const allScanTypesSelected = Object.values(scanTypes).every(v => v);
  const allResultsSelected = scanResults.length > 0 && selectedCount === scanResults.length;

  const toggleSelectAllScanTypes = () => {
    const newScanTypes = {};
    Object.keys(scanTypes).forEach(key => {
      newScanTypes[key] = !allScanTypesSelected;
    });
    setScanTypes(newScanTypes);
  };

  const toggleSelectAllResults = () => {
    if (allResultsSelected) {
      setSelectedIssues({});
    } else {
      const newSelected = {};
      scanResults.forEach((_, idx) => newSelected[idx] = true);
      setSelectedIssues(newSelected);
    }
  };

  // Function to linkify mission, objective, and activity names in descriptions
  const linkifyDescription = (description) => {
    if (!description) return <span>{description}</span>;

    const parts = [];

    // Find missions
    missions.forEach(mission => {
      const regex = new RegExp(`(Mission:?\\s*"?${mission.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"?)`, 'gi');
      const matches = [...description.matchAll(regex)];
      matches.forEach(match => {
        parts.push({ index: match.index, length: match[0].length, type: 'mission', id: mission.id, text: match[0] });
      });
    });

    // Find objectives (look for quoted statements)
    objectives.forEach(objective => {
      const shortStatement = objective.statement.substring(0, 50);
      const regex = new RegExp(`(Objective:?\\s*"${shortStatement.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^"]*")`, 'gi');
      const matches = [...description.matchAll(regex)];
      matches.forEach(match => {
        parts.push({ index: match.index, length: match[0].length, type: 'objective', id: objective.id, text: match[0] });
      });
    });

    // Find activities
    activities.forEach(activity => {
      const regex = new RegExp(`(Activity:?\\s*"?${activity.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"?)`, 'gi');
      const matches = [...description.matchAll(regex)];
      matches.forEach(match => {
        parts.push({ index: match.index, length: match[0].length, type: 'activity', id: activity.id, text: match[0] });
      });
    });

    // Sort by index and remove overlapping matches
    parts.sort((a, b) => a.index - b.index);
    const filtered = [];
    for (let i = 0; i < parts.length; i++) {
      if (i === 0 || parts[i].index >= filtered[filtered.length - 1].index + filtered[filtered.length - 1].length) {
        filtered.push(parts[i]);
      }
    }

    // Build the result with links
    if (filtered.length === 0) return <span>{description}</span>;

    const elements = [];
    let currentIndex = 0;

    filtered.forEach((part, idx) => {
      // Add text before the link
      if (part.index > currentIndex) {
        elements.push(
          <span key={`text-${idx}`}>{description.substring(currentIndex, part.index)}</span>
        );
      }

      // Add the link
      const linkUrl = part.type === 'mission' ? createPageUrl('missions') :
                      part.type === 'objective' ? createPageUrl('discovery') :
                      createPageUrl('activities');
      
      elements.push(
        <Link key={`link-${idx}`} to={linkUrl} className="text-indigo-400 hover:text-indigo-300 underline">
          {part.text}
        </Link>
      );

      currentIndex = part.index + part.length;
    });

    // Add remaining text
    if (currentIndex < description.length) {
      elements.push(
        <span key="text-end">{description.substring(currentIndex)}</span>
      );
    }

    return <>{elements}</>;
  };

  return (
    <ConfirmableDialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="issues-issuescandialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Scan for Issues</DialogTitle>
        </DialogHeader>

        {scanResults.length === 0 ? (
          <div className="space-y-6 mt-4">
            <p className="text-slate-400 text-sm">
              Select the types of issues you'd like to scan for in your system:
            </p>

            <div className="space-y-3">
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSelectAllScanTypes}
                  className="text-xs text-indigo-400 hover:text-indigo-300 h-6 px-2"
                >
                  {allScanTypesSelected ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              {Object.entries(scanTypeLabels).map(([key, label]) => (
                <div key={key} className="flex items-center space-x-3 p-3 bg-slate-950 rounded-lg border border-slate-800 hover:border-slate-700 transition-colors">
                  <Checkbox
                    id={key}
                    checked={scanTypes[key]}
                    onCheckedChange={(checked) => setScanTypes({ ...scanTypes, [key]: checked })}
                  />
                  <Label htmlFor={key} className="text-sm cursor-pointer flex-1">{label}</Label>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleScan}
                disabled={scanning || Object.values(scanTypes).every(v => !v)}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {scanning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Start Scan
                  </>
                )}
              </Button>
              <Button data-testid="issues-cancel-btn" onClick={onClose} variant="outline" className="border-slate-700">
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <p className="text-slate-400 text-sm">
                Found {scanResults.length} potential issue{scanResults.length !== 1 ? 's' : ''}. Select which to add:
              </p>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSelectAllResults}
                  className="text-indigo-400 hover:text-indigo-300"
                >
                  {allResultsSelected ? 'Deselect All' : 'Select All'}
                </Button>
                <Button data-testid="issues-start-over-btn" onClick={handleReset} variant="ghost" size="sm" className="text-slate-400">
                  <X className="w-4 h-4 mr-2" />
                  Start Over
                </Button>
              </div>
            </div>

            <div className="space-y-3 max-h-[50vh] overflow-y-auto">
              {scanResults.map((issue, idx) => (
                <Card key={idx} className="glass-panel border-slate-800">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id={`issue-${idx}`}
                        checked={selectedIssues[idx] || false}
                        onCheckedChange={(checked) => setSelectedIssues({ ...selectedIssues, [idx]: checked })}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <Label htmlFor={`issue-${idx}`} className="text-base font-semibold cursor-pointer">
                          {issue.title}
                        </Label>
                        <div className="text-sm text-slate-400 mt-1">
                          {linkifyDescription(issue.description)}
                        </div>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {issue.category || 'execution_headwind'}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {issue.severity || 'medium'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleAddSelectedIssues}
                disabled={selectedCount === 0 || createIssuesMutation.isPending}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              >
                {createIssuesMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Add Selected ({selectedCount})
                  </>
                )}
              </Button>
              <Button data-testid="issues-cancel-btn-1" onClick={onClose} variant="outline" className="border-slate-700">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </ConfirmableDialog>
  );
}